import { useEffect, useMemo, useState } from "react";

import { useMedplum } from "@medplum/react";

import {
  Drawer,
  Flex,
  Group,
  NumberInput,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { modals } from "@mantine/modals";

import { DisabledReasonButton, NO_CHANGES_REASON } from "@/components/DisabledReason";
import { TextWrapper } from "@/components/TextWrapper";
import { replaceFormSnapshotForDrawerDismiss } from "@/settings/utils/replace-form-snapshot-for-drawer-dismiss";
import { toast } from "@/utils/notifications";

import { CUSTOM_CODE_FORMAT_HINT } from "../constants/custom-codes";
import { checkCustomProcedureCodeAgainstCpt } from "../services/cpt-custom-procedure-code-conflict";
import { normalizeNumberInputValue } from "../services/custom-codes";
import { ProcedureCode } from "../types/fee-table";
import { CustomCodeFormSchema, CustomCodeFormType } from "../zod-schemas/custom-code";

const currencyInputStyles = {
  label: {
    marginBottom: 8,
  },
  wrapper: {
    display: "flex",
    alignItems: "center",
  },
  section: {
    width: 49,
    height: 36,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "var(--mantine-color-gray-1)",
    borderRadius: "2px 0 0 2px",
    border: "1px solid var(--mantine-color-gray-5)",
    borderRight: "none",
    color: "var(--mantine-color-gray-7)",
    position: "static",
    marginRight: 0,
  },
  input: {
    height: 36,
    flex: 1,
    borderRadius: "0 2px 2px 0",
    border: "1px solid var(--mantine-color-gray-5)",
    background: "#FFF",
    color: "var(--mantine-color-dark-9)",
    paddingLeft: 12,
  },
} as const;

interface CustomCodeDrawerProps {
  opened: boolean;
  onClose: () => void;
  procedure: ProcedureCode | null;
  /** Used when creating: if `code` matches an existing custom procedure, a confirm modal is shown before overwriting. */
  existingCustomCodes: ProcedureCode[];
  onSave: (formValues: CustomCodeFormType, existingCode?: string) => Promise<void>;
}

function getInitialValues(procedure: ProcedureCode | null): CustomCodeFormType {
  return {
    code: procedure?.code ?? "",
    name: procedure?.name ?? "",
    information: procedure?.information ?? "",
    insuranceFee: procedure?.insuranceFee ?? null,
    selfPayFee: procedure?.selfPayFee ?? null,
    isActive: procedure?.isActive ?? true,
  };
}

export default function CustomCodeDrawer({
  opened,
  onClose,
  procedure,
  existingCustomCodes,
  onSave,
}: CustomCodeDrawerProps) {
  const medplum = useMedplum();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!procedure;

  const initialValues = useMemo(() => getInitialValues(procedure), [procedure]);

  const form = useForm<CustomCodeFormType>({
    initialValues,
    validate: zodResolver(CustomCodeFormSchema),
  });

  useEffect(() => {
    if (opened) {
      const values = getInitialValues(procedure);
      form.setValues(values);
      form.resetDirty(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, procedure]);

  const handleSubmit = async (values: CustomCodeFormType) => {
    if (isSubmitting) return;

    const existingCode = isEditing ? procedure.code : undefined;

    setIsSubmitting(true);
    try {
      if (!isEditing) {
        const standardProcedureCodeValidation = await checkCustomProcedureCodeAgainstCpt(
          medplum,
          values.code,
        );

        if (standardProcedureCodeValidation.status === "verification_failed") {
          toast({
            type: "warning",
            title: "Could not verify against standard procedure codes",
            message:
              "The standard procedure terminology list could not be checked right now. You can still save this custom code. Confirm it is not already a standard billing or procedure code, or use a distinct practice code such as 99214a or 99214b.",
            autoClose: false,
          });
        } else if (
          standardProcedureCodeValidation.truncated &&
          !standardProcedureCodeValidation.conflicts
        ) {
          toast({
            type: "warning",
            title: "Standard code check was incomplete",
            message:
              "Many codes matched this search, so we could not fully confirm this code is not already standard. If unsure, use a practice-specific suffix (for example 99214a).",
            autoClose: false,
          });
        }

        if (
          standardProcedureCodeValidation.status === "verified" &&
          standardProcedureCodeValidation.conflicts
        ) {
          form.setFieldError(
            "code",
            "This code is already a standard CPT or HCPCS code. Use a distinct custom code. Example: 99214 is a standard code; 99214a or 99214b can be practice specific custom codes.",
          );
          return;
        }

        const collision = existingCustomCodes.find(
          (existingCustomProcedure) => existingCustomProcedure.code === values.code,
        );
        if (collision) {
          modals.openConfirmModal({
            title: "Replace existing custom code?",
            children: (
              <Text size="sm">
                {`This practice already has a custom procedure with code ${collision.code} (${collision.name}). Saving will replace that entry and its fees with the values in this form. Continue?`}
              </Text>
            ),
            labels: { confirm: "Replace existing", cancel: "Go back" },
            confirmProps: { color: "red" },
            onConfirm: async () => {
              setIsSubmitting(true);
              try {
                await onSave(values, existingCode);
                handleClose();
              } finally {
                setIsSubmitting(false);
              }
            },
          });
          return;
        }
      }

      await onSave(values, existingCode);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    replaceFormSnapshotForDrawerDismiss(form, getInitialValues(null));
    onClose();
  };

  const hasChanges = form.isDirty();

  return (
    <Drawer.Root
      trapFocus
      size={500}
      opened={opened}
      position="right"
      onClose={handleClose}
      closeOnEscape={false}
      closeOnClickOutside={false}
    >
      <Drawer.Overlay />
      <Drawer.Content bg="white" data-testid="custom-code-drawer">
        <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
          <Drawer.Header h={64} px="md" bg="gray.0">
            <Flex w="100%" gap="md" align="center" justify="space-between">
              <Flex gap="md" align="center" style={{ flex: 1 }}>
                <Drawer.CloseButton
                  aria-label="Close Custom Code Drawer"
                  data-testid="custom-code-drawer-close-button"
                />
                <Drawer.Title style={{ flex: 1 }}>
                  <TextWrapper fz="lg" c="dark.9" variant="bold">
                    {isEditing ? "Edit Custom Code" : "Create Custom Code"}
                  </TextWrapper>
                </Drawer.Title>
              </Flex>
              <DisabledReasonButton
                type="submit"
                loading={isSubmitting}
                data-testid="custom-code-drawer-submit-button"
                // Create stays pressable so an empty submit names the required fields.
                disabledReason={isEditing && !hasChanges ? NO_CHANGES_REASON : undefined}
              >
                {isEditing ? "Save" : "Create"}
              </DisabledReasonButton>
            </Flex>
          </Drawer.Header>
          <Drawer.Body p={24}>
            <Stack gap={24}>
              <TextInput
                required
                label="Code"
                disabled={isSubmitting || isEditing} // Code cannot be changed when editing
                placeholder={CUSTOM_CODE_FORMAT_HINT}
                data-testid="custom-code-drawer-code-input"
                description={isEditing ? "Code cannot be changed after creation" : undefined}
                {...form.getInputProps("code")}
              />

              <TextInput
                required
                label="Code Name"
                disabled={isSubmitting}
                placeholder="Enter code name"
                data-testid="custom-code-drawer-name-input"
                {...form.getInputProps("name")}
              />

              <Textarea
                minRows={3}
                disabled={isSubmitting}
                label="Code Information"
                placeholder="Enter code information (optional)"
                data-testid="custom-code-drawer-information-input"
                {...form.getInputProps("information")}
              />

              <NumberInput
                w="100%"
                hideControls
                leftSection="$"
                placeholder="0"
                decimalScale={2}
                label="Insurance Fee"
                leftSectionWidth={49}
                allowNegative={false}
                thousandSeparator=","
                disabled={isSubmitting}
                styles={currencyInputStyles}
                leftSectionPointerEvents="none"
                data-testid="custom-code-drawer-insurance-fee-input"
                {...form.getInputProps("insuranceFee", {
                  withError: true,
                  onChange: (value: string | number | undefined) => {
                    form.setFieldValue("insuranceFee", normalizeNumberInputValue(value));
                  },
                })}
              />

              <NumberInput
                w="100%"
                hideControls
                leftSection="$"
                placeholder="0"
                decimalScale={2}
                label="Self Pay Fee"
                leftSectionWidth={49}
                allowNegative={false}
                thousandSeparator=","
                disabled={isSubmitting}
                styles={currencyInputStyles}
                leftSectionPointerEvents="none"
                data-testid="custom-code-drawer-self-pay-fee-input"
                {...form.getInputProps("selfPayFee", {
                  withError: true,
                  onChange: (value: string | number | undefined) => {
                    form.setFieldValue("selfPayFee", normalizeNumberInputValue(value));
                  },
                })}
              />

              <Group
                p="md"
                w="100%"
                justify="space-between"
                style={{ border: "1px solid var(--mantine-color-gray-3)", borderRadius: 8 }}
              >
                <Group gap="xs">
                  <TextWrapper c="dark.9" variant="bold">
                    Status
                  </TextWrapper>
                  <TextWrapper
                    variant="bold"
                    c={form.values.isActive ? "green.7" : "gray.6"}
                    bg={form.values.isActive ? "green.0" : "gray.1"}
                  >
                    {form.values.isActive ? "Active" : "Inactive"}
                  </TextWrapper>
                </Group>
                <Switch
                  disabled={isSubmitting}
                  checked={form.values.isActive}
                  aria-label="Toggle custom code status"
                  data-testid="custom-code-drawer-status-toggle"
                  onChange={(event) => form.setFieldValue("isActive", event.currentTarget.checked)}
                />
              </Group>
            </Stack>
          </Drawer.Body>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
}
